/**
 * @file Core API service.
 * @description Centralized request helpers and typed endpoint accessors for dropdowns, auth, and shared APIs.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { API_ENDPOINTS } from '../constants/api';
import { buildApiUrl } from '../utils/api';
import { dedupeGetRequest } from '../utils/requestDedupe';
import { AdvertisersService } from './advertisersService';
import { CampaignsService } from './campaignsService';
import type { ApiValidationError } from '../utils/apiValidation';
import type {
  ServiceApiResponse as ApiResponse,
  DropdownItem,
  ServiceUser as User,
  ServiceLead,
  ServiceLeadsApiResponse as LeadsApiResponse,
  ServiceLeadsFilters as LeadsFilters,
  ServiceLeadsResult as LeadsResult,
} from '../types/api/serviceApi';

export type {
  ServiceApiResponse as ApiResponse,
  DropdownItem,
  ServiceUser as User,
  ServiceLeadsApiResponse as LeadsApiResponse,
  ServiceLeadsFilters as LeadsFilters,
  ServiceLeadsResult as LeadsResult,
} from '../types/api/serviceApi';
export type { ServiceLead as Lead } from '../types/api/serviceApi';

function throwApiValidationError(errorResult: {
  errors?: Record<string, string | string[]>;
  success?: boolean;
  message?: string;
}): never {
  const validationError = new Error(errorResult.message || 'Validation failed') as ApiValidationError;
  validationError.errors = errorResult.errors ?? {};
  validationError.success = errorResult.success;
  throw validationError;
}

/** Normalize API payloads that may be an array, wrapped in `data`, or nested under common keys. */
function normalizeListResponse<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    for (const key of ['data', 'advertisers', 'campaigns', 'sources', 'items', 'records']) {
      const v = obj[key];
      if (Array.isArray(v)) return v as T[];
      if (key === 'data' && v && typeof v === 'object') {
        const inner = (v as Record<string, unknown>)['data'];
        if (Array.isArray(inner)) return inner as T[];
      }
    }
    const data = obj['data'];
    if (data && typeof data === 'object') {
      const nested = data as Record<string, unknown>;
      for (const key of ['advertisers', 'campaigns', 'sources', 'items', 'records', 'data']) {
        const v = nested[key];
        if (Array.isArray(v)) return v as T[];
      }
    }
  }
  return [];
}

function strField(value: unknown): string {
  return value == null ? '' : String(value);
}

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Map API row (Name/name, etc.) to ServiceLead for LeadTable. */
function normalizeServiceLead(row: unknown): ServiceLead {
  const r = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const lead: ServiceLead = {
    id: strField(r['id'] ?? r['lead_id']),
    name: strField(r['name'] ?? r['Name']),
    phone: strField(r['phone'] ?? r['Phone']),
    dob: (r['dob'] as string | null) ?? null,
    age: numOrNull(r['age'] ?? r['Age']),
    city: strField(r['city'] ?? r['City']),
    income: numOrNull(r['income'] ?? r['Income']),
    email: strField(r['email'] ?? r['Email']),
    source: strField(r['source'] ?? r['Source'] ?? r['source_name']),
    source_name: strField(r['source_name'] ?? r['Source'] ?? r['source']),
    created_at: strField(r['created_at'] ?? r['uploaded_at']),
    advertiser_name: strField(r['advertiser_name'] ?? r['Advertiser']),
    campaign_name: strField(r['campaign_name'] ?? r['Campaign']),
    company_name: strField(
      r['company_name'] ?? r['Company Name'] ?? r['Company'] ?? r['compan_name'] ?? r['company'],
    ),
    address: strField(r['address'] ?? r['Address']),
    vehicle_brand_name: strField(r['vehicle_brand_name'] ?? r['Vehicle Brand Name'] ?? r['VehicleBrandName']),
    dnd_status: strField(r['dnd_status'] ?? r['DND Status'] ?? r['DndStatus']),
    plan: strField(r['plan'] ?? r['Plan']),
    user_id: strField(r['user_id']),
    pincode: (r['pincode'] as string | null) ?? null,
    updated_at: (r['updated_at'] as string | null) ?? null,
    deleted_at: (r['deleted_at'] as string | null) ?? null,
  };
  if (r['data_type'] != null) lead.data_type = strField(r['data_type']);
  if (r['status'] != null) lead.status = strField(r['status']);
  return lead;
}

/**
 * view-leads: `{ success, status, message, pagination, data: Lead[] }`
 * pagination: `{ page, per_page, total, total_page }`
 */
function normalizeViewLeadsResponse(
  raw: unknown,
  fallbackPerPage: number,
): {
  leads: ServiceLead[];
  pagination: { page: number; per_page: number; total: number; total_page: number };
} {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  const pagSource =
    root['pagination'] ??
    (root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)['pagination']
      : undefined);

  const pag =
    pagSource && typeof pagSource === 'object'
      ? (pagSource as Record<string, unknown>)
      : {};

  const page = Number(pag['page'] ?? root['current_page'] ?? 1) || 1;
  const per_page =
    Number(pag['per_page'] ?? root['per_page'] ?? fallbackPerPage) || fallbackPerPage;
  const total =
    Number(pag['total'] ?? pag['total_records'] ?? root['total_records'] ?? root['total'] ?? 0) ||
    0;
  let total_page =
    Number(
      pag['total_page'] ?? pag['total_pages'] ?? root['total_pages'] ?? root['total_page'] ?? 0,
    ) || 0;
  if (total_page === 0 && total > 0 && per_page > 0) {
    total_page = Math.ceil(total / per_page);
  }

  let rows: unknown[] = [];
  const data = root['data'];
  if (Array.isArray(data)) {
    rows = data;
  } else if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['data'])) rows = inner['data'];
    else if (Array.isArray(inner['leads'])) rows = inner['leads'];
    else if (Array.isArray(inner['records'])) rows = inner['records'];
    else if (Array.isArray(inner['items'])) rows = inner['items'];
  }
  if (rows.length === 0 && Array.isArray(root['leads'])) {
    rows = root['leads'];
  }
  if (rows.length === 0 && Array.isArray(root['records'])) {
    rows = root['records'];
  }

  return {
    leads: rows.map(normalizeServiceLead),
    pagination: { page, per_page, total, total_page },
  };
}

function normFilterText(value: string): string {
  return value.trim().toLowerCase();
}

function hasLeadFieldFilters(filters: LeadsFilters): boolean {
  return Boolean(
    filters.company_name?.trim() ||
      filters.vehicle_brand_name?.trim() ||
      filters.dnd_status?.trim(),
  );
}

function matchesDndFilter(leadDnd: string, filterValue: string): boolean {
  const lead = normFilterText(leadDnd);
  const filter = normFilterText(filterValue);
  if (filter === 'yes') {
    return lead === 'yes' || lead.startsWith('yes');
  }
  if (filter === 'no') {
    return lead === 'no' || lead === 'na' || lead === 'n/a' || lead.startsWith('no');
  }
  return lead === filter;
}

/** Apply company / brand / DND filters on API rows when backend ignores query params. */
function applyLeadFieldFilters(leads: ServiceLead[], filters: LeadsFilters): ServiceLead[] {
  let result = leads;

  const company = filters.company_name?.trim();
  if (company) {
    const q = normFilterText(company);
    result = result.filter((lead) => normFilterText(lead.company_name || '').includes(q));
  }

  const brand = filters.vehicle_brand_name?.trim();
  if (brand) {
    const q = normFilterText(brand);
    result = result.filter((lead) => normFilterText(lead.vehicle_brand_name || '').includes(q));
  }

  const dnd = filters.dnd_status?.trim();
  if (dnd) {
    result = result.filter((lead) => matchesDndFilter(lead.dnd_status || '', dnd));
  }

  return result;
}

/**
 * Unified API Service
 * Handles all API calls with consistent error handling and response formatting
 */
export class ApiService {
  private static readonly DEFAULT_PER_PAGE = 10;
  private static readonly MAX_PER_PAGE = 100;

  /**
   * Get authentication token
   */
  private static getToken(): string | null {
    return localStorage.getItem('jiggle_token');
  }

  /**
   * Remove authentication token
   */
  private static removeToken(): void {
    localStorage.removeItem('jiggle_token');
  }

  /**
   * Create headers for API requests
   */
  private static createHeaders(): Headers {
    const headers = new Headers();
    const token = this.getToken();
    
    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }
    
    return headers;
  }

  /**
   * Make API request with consistent error handling
   */
  static async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: FormData | URLSearchParams | Record<string, unknown>,
  ): Promise<T> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    const headers = this.createHeaders();
    const requestOptions: RequestInit = {
      method,
      headers,
      redirect: 'follow' as RequestRedirect,
    };

    if (body) {
      if (body instanceof FormData) {
        requestOptions.body = body;
      } else if (body instanceof URLSearchParams) {
        headers.append('Content-Type', 'application/x-www-form-urlencoded');
        requestOptions.body = body;
      } else {
        headers.append('Content-Type', 'application/json');
        const jsonBody = JSON.stringify(body);
        requestOptions.body = jsonBody;
      }
    }

    const url = buildApiUrl(endpoint);

    const runFetch = async (): Promise<T> => {
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        try {
          const errorResult = await response.json();

          if (errorResult.errors && typeof errorResult.errors === 'object') {
            throwApiValidationError(errorResult);
          }

          if (errorResult.status === 'error' && errorResult.message === 'Invalid or expired token') {
            this.removeToken();
            localStorage.removeItem('jiggle_user');
            window.location.href = '/login';
            throw new Error('Session expired. Please login again.');
          }

          throw new Error(errorResult.message || `HTTP ${response.status}: ${response.statusText}`);
        } catch (err) {
          if (err && typeof err === 'object' && 'errors' in err) {
            throw err;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      const result = await response.json();

      if (result.status === 'error' || result.success === false) {
        if (result.errors && typeof result.errors === 'object') {
          throwApiValidationError(result);
        }
        if (result.message === 'Invalid or expired token') {
          this.removeToken();
          localStorage.removeItem('jiggle_user');
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(result.message || 'API returned an error');
      }

      return result;
    };

    if (method === 'GET') {
      return dedupeGetRequest(url, runFetch);
    }

    return runFetch();
  }

  /**
   * Build query parameters
   */
  private static buildQueryParams(params: Record<string, string | number>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        searchParams.append(key, String(value));
      }
    });
    
    return searchParams.toString();
  }

  // ==================== LEADS API ====================

  /**
   * Fetch leads with pagination and filters
   */
  static async getLeads(filters: LeadsFilters = {}, page: number = 1, perPage: number = this.DEFAULT_PER_PAGE): Promise<LeadsResult> {
    try {
      const fieldFiltersActive = hasLeadFieldFilters(filters);
      const requestPage = fieldFiltersActive ? 1 : page;
      const requestPerPage = fieldFiltersActive
        ? this.MAX_PER_PAGE
        : Math.min(perPage, this.MAX_PER_PAGE);

      const params: Record<string, string | number> = {
        action: 'preview',
        page: requestPage,
        per_page: requestPerPage,
        data_type:
          filters['data_type'] && String(filters['data_type']).trim() !== ''
            ? String(filters['data_type']).trim()
            : 'lead',
      };

      // Add filters (must match FilterBar `handleApplyFilter` keys)
      if (filters['advertiser_id']) params['advertiser_id'] = filters['advertiser_id'];
      if (filters['campaign_id']) params['campaign_id'] = filters['campaign_id'];
      if (filters['source']) params['source'] = filters['source'];
      // Backend date range (Export uses from_date/to_date on view-leads-filter)
      if (filters['date_from']) {
        const from = filters['date_from'];
        params['from_date'] = from;
        params['date_from'] = from;
      }
      if (filters['date_to']) {
        const to = filters['date_to'];
        params['to_date'] = to;
        params['date_to'] = to;
      }
      if (filters['min_age']) params['min_age'] = filters['min_age'];
      if (filters['max_age']) params['max_age'] = filters['max_age'];
      if (filters['min_income']) params['min_income'] = filters['min_income'];
      if (filters['max_income']) params['max_income'] = filters['max_income'];
      if (filters['company_name']) params['company_name'] = filters['company_name'];
      if (filters['vehicle_brand_name']) params['vehicle_brand_name'] = filters['vehicle_brand_name'];
      if (filters['dnd_status']) params['dnd_status'] = filters['dnd_status'];

      const queryParams = this.buildQueryParams(params);
      const response = await this.makeRequest<LeadsApiResponse | Record<string, unknown>>(
        `${API_ENDPOINTS.LEADS_VIEW}?${queryParams}`,
      );

      let { leads, pagination } = normalizeViewLeadsResponse(response, requestPerPage);

      // Company / brand / DND: API params sent but backend may not filter — refine client-side
      if (fieldFiltersActive) {
        const filtered = applyLeadFieldFilters(leads, filters);
        const total = filtered.length;
        const totalPages = total > 0 ? Math.ceil(total / perPage) : 0;
        const safePage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
        const start = (safePage - 1) * perPage;
        leads = filtered.slice(start, start + perPage);
        pagination = {
          page: safePage,
          per_page: perPage,
          total,
          total_page: totalPages,
        };
      }

      return {
        leads,
        pagination: {
          totalRecords: pagination.total,
          totalPages: pagination.total_page,
          currentPage: pagination.page,
          perPage: pagination.per_page,
        },
      };
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch leads');
    }
  }

  // ==================== DROPDOWN DATA API ====================

  /**
   * Fetch advertisers for select fields (GET /advertisers/list).
   */
  static async getAdvertisers(): Promise<DropdownItem[]> {
    try {
      const advertisers = await AdvertisersService.getAdvertisers();
      return advertisers.map((advertiser) => ({
        id: advertiser.id,
        name: advertiser.name,
      }));
    } catch {
      return [];
    }
  }

  private static campaignsDropdownCache: DropdownItem[] | null = null;
  private static campaignsDropdownPromise: Promise<DropdownItem[]> | null = null;

  /**
   * Fetch campaigns (dropdowns). Cached to avoid duplicate calls on page load.
   */
  static async getCampaigns(): Promise<DropdownItem[]> {
    if (ApiService.campaignsDropdownCache) {
      return ApiService.campaignsDropdownCache;
    }
    if (!ApiService.campaignsDropdownPromise) {
      ApiService.campaignsDropdownPromise = (async () => {
        const response = await this.makeRequest<ApiResponse<DropdownItem[] | Record<string, unknown>>>(
          API_ENDPOINTS.CAMPAIGNS,
        );
        const list = normalizeListResponse<DropdownItem>(response.data ?? response);
        ApiService.campaignsDropdownCache = list;
        return list;
      })().catch(() => {
        ApiService.campaignsDropdownPromise = null;
        return [];
      });
    }
    return ApiService.campaignsDropdownPromise;
  }

  /**
   * Fetch campaigns for one advertiser (GET /campaigns/advertiser/{id}).
   */
  static async getCampaignsByAdvertiser(advertiserId: string): Promise<DropdownItem[]> {
    try {
      const campaigns = await CampaignsService.getCampaignsByAdvertiser(advertiserId);
      return campaigns.map((campaign) => ({
        id: campaign.id,
        name: campaign.name,
      }));
    } catch {
      return [];
    }
  }

  /**
   * Fetch sources for select/dropdown fields (GET /sources/list).
   * Backend returns active sources only.
   */
  static async getSources(): Promise<DropdownItem[]> {
    try {
      const response = await this.makeRequest<ApiResponse<DropdownItem[] | Record<string, unknown>>>(
        API_ENDPOINTS.SOURCES_LIST,
      );
      return normalizeListResponse<DropdownItem>(response.data ?? response);
    } catch {
      return [];
    }
  }

  /**
   * Fetch all dropdown data in parallel
   */
  static async getDropdownData(): Promise<{
    advertisers: DropdownItem[];
    campaigns: DropdownItem[];
    sources: DropdownItem[];
  }> {
    try {
      const [advertisersResponse, campaignsResponse, sourcesResponse] = await Promise.all([
        this.getAdvertisers(),
        this.getCampaigns(),
        this.getSources(),
      ]);
     
      const advertisers = advertisersResponse;
      const campaigns = campaignsResponse;
      const sources = sourcesResponse;

      return { advertisers, campaigns, sources };
    } catch {
      return { advertisers: [], campaigns: [], sources: [] };
    }
  }

  // ==================== AUTHENTICATION API ====================

  /**
   * Login user (no token required)
   */
  static async login(credentials: { email: string; password: string }): Promise<ApiResponse<{ token?: string; access_token?: string; user?: User }>> {
    try {
      // Login must not include any stale Authorization header.
      const headers = new Headers();
      headers.set('Content-Type', 'application/json');

      const requestOptions: RequestInit = {
        method: 'POST',
        headers,
        redirect: 'follow' as RequestRedirect,
        body: JSON.stringify(credentials),
      };

      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOGIN), requestOptions);
      
      if (!response.ok) {
        try {
          const errorResult = await response.json();
          throw new Error(errorResult.message || `HTTP ${response.status}: ${response.statusText}`);
        } catch {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      const result = await response.json();
      
      if (result.status === 'error') {
        throw new Error(result.message || 'API returned an error');
      }
      
      const token = result?.data?.access_token || result?.data?.token;
      if (result.status === 'success' && token) {
        localStorage.setItem('jiggle_token', token);
      }

      if (result?.data?.user) {
        localStorage.setItem('jiggle_user', JSON.stringify(result.data.user));
      }
      
      return result;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Login failed');
    }
  }

  /**
   * Logout user
   */
  static logout(): void {
    localStorage.removeItem('jiggle_token');
    localStorage.removeItem('jiggle_user');
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Get current user
   */
  static getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('jiggle_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }
}
